'use client';

import { useState, useRef, JSX } from 'react';
import { useEvent, useMount } from 'react-use';
import { twMerge } from 'tailwind-merge';

import { SearchIdentifiers } from '@videoblocks/events-ts/lib/storyblocks/search/records/SearchIdentifiers';
import { AudioStockItem } from '@videoblocks/search-gateway-client';

import StockItemCardContext from '../../cards/AudioStockItemCard/context/StockItemCardContext';
import AudioDuration from '../../elements/AudioDuration';
import { AudioPlayerForwardButton } from '../../elements/AudioPlayer/AudioPlayerForwardButton';
import { AudioPlayerRewindButton } from '../../elements/AudioPlayer/AudioPlayerRewindButton';
import { AudioPlayerVolumeControl } from '../../elements/AudioPlayer/AudioPlayerVolumeControl';
import AudioPlayerArtists from '../../elements/AudioPlayerArtists';
import AudioPlayerTitle from '../../elements/AudioPlayerTitle';
import { StockItemContext, StockItemUserData } from '../../elements/StockItemTypes';
import Waveform from '../../elements/Waveform';
import AddToFolderButton from '../../elements/buttons/AddToFolderButton';
import AudioPlayerPlayButton from '../../elements/buttons/AudioPlayerPlayButton';
import DownloadButton from '../../elements/buttons/DownloadButton';
import SearchSimilarButton from '../../elements/buttons/SearchSimilarButton';
import { AUDIO_EVENTS, PlayEvent, VolumeChangedEvent } from '../../events/AudioEvents';

export interface GlobalAudioPlayerProps {
    stockItem: AudioStockItem;
    context?: StockItemContext;
    onPlayerNextClick: () => void;
    onPlayerPreviousClick: () => void;
    handleAudioItemClick: (isPlaying: boolean) => void;
    position?: number; // i.e. index in search results
    userData: StockItemUserData;
    className?: string;
    isPlaying?: boolean;
    onError?: (message: string) => void;
    searchIdentifiers?: SearchIdentifiers;
}

export const GlobalAudioPlayer = ({
    stockItem,
    isPlaying,
    context = StockItemContext.SEARCH,
    onPlayerNextClick,
    onPlayerPreviousClick,
    handleAudioItemClick,
    userData,
    className,
    onError,
    searchIdentifiers,
}: GlobalAudioPlayerProps): JSX.Element => {
    /**
     * NOTE: this state could be lifted up to its wrapper if AudioStockItemCard needs this as well.
     * However, currently only GlobalAudioPlayer needs to track this
     *  */
    const [currentVolume, setCurrentVolume] = useState(0.5);
    const [currentTime, setCurrentTime] = useState(0);
    const [isLoggedIn, setIsLoggedIn] = useState(userData.isLoggedIn);
    const [isHovered, setIsHovered] = useState(false);

    useEvent(AUDIO_EVENTS.TIMEUPDATE, (event: Event) => {
        const playEvent = event as PlayEvent;
        if (playEvent.src === stockItem.previewUrl) {
            setCurrentTime(playEvent.time || 0);
        }
    });

    useMount(() => {
        window.dispatchEvent(new VolumeChangedEvent(stockItem.previewUrl, currentVolume));
    });

    const onVolumeChange = (volume: number) => {
        window.dispatchEvent(new VolumeChangedEvent(stockItem.previewUrl, volume));
        setCurrentVolume(volume);
    };

    const handleOnPlayButtonClick = () => {
        if (stockItem) {
            handleAudioItemClick(!isPlaying);
        }
    };

    const sectionRef = useRef<HTMLDivElement>(null);

    return (
        <StockItemCardContext.Provider value={{ stockItemContext: context }}>
            <section
                data-testid="global-audio-player-card"
                aria-label="Global Audio Player"
                className={twMerge(
                    'group flex w-full m-0 p-0 z-auto text-white lg:h-24 gap-x-4 items-center py-2 px-6 bg-gray-700',
                    className,
                )}
                ref={sectionRef}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={(e) => {
                    const related = e.relatedTarget as HTMLElement | null;
                    if (related && related instanceof HTMLElement && sectionRef.current?.contains(related)) {
                        return;
                    }
                    // i.e. refresh folders
                    setIsHovered(false);
                }}
            >
                <div className={'flex w-full items-center lg:w-1/3 lg:py-0 gap-x-4'}>
                    <div className="flex gap-x-4 items-center">
                        <AudioPlayerRewindButton onClick={onPlayerPreviousClick} className="hidden lg:block" />
                        <AudioPlayerPlayButton
                            isPlaying={isPlaying}
                            onClick={handleOnPlayButtonClick}
                            className="p-0 lg:p-x-4"
                        />
                        <AudioPlayerForwardButton onClick={onPlayerNextClick} />
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                        <AudioPlayerTitle stockItem={stockItem} wrapperClassName="text-white" />
                        <AudioPlayerArtists stockItem={stockItem} wrapperClassName="text-white" />
                    </div>
                </div>
                <div className="flex w-full h-16 gap-x-4 lg:w-2/3 lg:gap-x-6 items-center">
                    <div className="hidden lg:flex w-full h-full gap-x-4 items-center">
                        <AudioDuration duration={currentTime} className="lg:order-1" />
                        <Waveform
                            stockItem={stockItem}
                            isPlaying={!!isPlaying}
                            handleAudioItemClick={handleAudioItemClick}
                            className="lg:order-2"
                        />
                        <AudioDuration duration={stockItem.duration} className="lg:order-3" />
                    </div>
                    <div className="hidden lg:block max-w-[104px] w-full h-full">
                        <AudioPlayerVolumeControl currentVolume={currentVolume} onVolumeChange={onVolumeChange} />
                    </div>
                    <div className="flex items-center gap-x-4 lg:gap-x-6 order-last text-white">
                        <SearchSimilarButton
                            isDisabled={!stockItem.hasVector || context !== StockItemContext.SEARCH}
                            selectedContentType={stockItem.contentType}
                            contentId={stockItem.id}
                            className="text-inherit"
                        />
                        <AddToFolderButton
                            folderGroup={userData.folderGroup}
                            isLoggedIn={isLoggedIn}
                            rootHovered={isHovered}
                            className="text-white"
                            stockItem={stockItem}
                            searchIdentifiers={searchIdentifiers}
                        />
                        <DownloadButton
                            contentID={stockItem.id}
                            stockblocksId={stockItem.stockblocksId}
                            isLoggedIn={userData.isLoggedIn}
                            downloadGroup={userData.downloadGroup}
                            previewUrl={stockItem.previewUrl}
                            detailsUrl={stockItem.detailsUrl}
                            rootHovered={false}
                            onError={onError}
                            iconClassName="w-6 h-6"
                            stockItem={stockItem}
                        />
                    </div>
                </div>
            </section>
        </StockItemCardContext.Provider>
    );
};
